import React from "react";
import { TAB_LOCK_STATUS } from "../../hooks/useExamTabLock";
import "./ExamTabConflictDialog.css";

/**
 * Dialog shown when exam is already open in another tab/browser
 * Offers option to take over or cancel
 */
const ExamTabConflictDialog = ({
  status,
  conflict,
  kickedMessage,
  onTakeover,
  onCancel,
}) => {
  // Show conflict dialog
  if (status === TAB_LOCK_STATUS.CONFLICT && conflict) {
    return (
      <div className="tab-conflict-overlay">
        <div className="tab-conflict-dialog">
          <div className="tab-conflict-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>

          <h2 className="tab-conflict-title">Exam Already Open</h2>

          <p className="tab-conflict-message">
            This exam is currently open in another tab or browser.
          </p>

          {conflict.device && (
            <div className="tab-conflict-device">
              <p>
                <strong>Active session:</strong>
              </p>
              <p>
                {conflict.device.browser} on {conflict.device.os}
              </p>
              {conflict.lockedAt && (
                <p className="tab-conflict-time">
                  Started: {new Date(conflict.lockedAt).toLocaleTimeString()}
                </p>
              )}
            </div>
          )}

          <p className="tab-conflict-warning">
            Only one tab can be active at a time. If you continue here, the
            other tab will be disconnected.
          </p>

          <div className="tab-conflict-actions">
            <button
              className="tab-conflict-btn tab-conflict-btn-primary"
              onClick={onTakeover}
            >
              Continue Here
            </button>
            <button
              className="tab-conflict-btn tab-conflict-btn-secondary"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show kicked message
  if (status === TAB_LOCK_STATUS.KICKED) {
    return (
      <div className="tab-conflict-overlay">
        <div className="tab-conflict-dialog tab-conflict-kicked">
          <div className="tab-conflict-icon tab-conflict-icon-error">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>

          <h2 className="tab-conflict-title">Session Disconnected</h2>

          <p className="tab-conflict-message">
            {kickedMessage ||
              "You have been disconnected because the exam was opened in another tab or browser."}
          </p>

          <p className="tab-conflict-info">
            Your answers have been saved. You can close this tab or refresh to
            reconnect.
          </p>

          <div className="tab-conflict-actions">
            <button
              className="tab-conflict-btn tab-conflict-btn-primary"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show disconnected message (network issue)
  if (status === TAB_LOCK_STATUS.DISCONNECTED) {
    return (
      <div className="tab-conflict-overlay tab-conflict-overlay-warning">
        <div className="tab-conflict-dialog tab-conflict-disconnected">
          <div className="tab-conflict-icon tab-conflict-icon-warning">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="1" y1="1" x2="23" y2="23" />
              <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
              <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
              <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
              <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
              <line x1="12" y1="20" x2="12.01" y2="20" />
            </svg>
          </div>

          <h2 className="tab-conflict-title">Connection Lost</h2>

          <p className="tab-conflict-message">
            Attempting to reconnect... Please check your internet connection.
          </p>

          <div className="tab-conflict-loader">
            <div className="tab-conflict-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ExamTabConflictDialog;
